/**
 * Production Planning — calendar / Gantt view.
 *
 * Layout:
 *   - factory warehouse Select at top
 *   - middle: weekly Gantt grid (rows = WOs, columns = days), bars
 *     coloured by status, click → opens WO detail Sheet
 *   - bottom: capacity summary (planned hours / scheduled hours /
 *     utilisation %, with a conflict warning when scheduled > planned)
 *
 * Pure SVG/HTML grid — no chart libraries. Bars are rendered via CSS
 * positioning calculated from `plannedStartAt` / `plannedEndAt` mapped to
 * the visible 7-day window.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";

import {
  Badge,
  Button,
  KpiCard,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { WorkOrderStatusBadge } from "../../../components/production/WorkOrderStatusBadge";
import { PriorityBadge } from "../../../components/production/PriorityBadge";
import {
  mockWorkOrders,
  type MockWorkOrder,
  type WorkOrderStatus,
} from "../../../lib/productionMock";
import {
  mockProducts,
  mockUnits,
  type MockProduct,
  type MockUnit,
} from "../../../lib/catalogMock";
import {
  mockWarehouses,
  type MockWarehouse,
} from "../../../lib/inventoryMock";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";

export const Route = createFileRoute("/_app/production/planning")({
  component: PlanningPage,
});

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

const STATUS_BAR_BG: Record<WorkOrderStatus, string> = {
  planned: "var(--bg-sunken)",
  in_progress: "var(--info-soft)",
  paused: "var(--warning-soft)",
  completed: "var(--success-soft)",
  cancelled: "var(--bg-canvas)",
};
const STATUS_BAR_BORDER: Record<WorkOrderStatus, string> = {
  planned: "var(--border-strong)",
  in_progress: "var(--info)",
  paused: "var(--warning)",
  completed: "var(--success)",
  cancelled: "var(--text-tertiary)",
};

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function dayLabel(ms: number): string {
  return new Date(ms).toLocaleDateString("en-BD", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

function PlanningPage() {
  const [workOrders] = usePersistentMockState("workOrders", mockWorkOrders);
  const [products] = usePersistentMockState<MockProduct[]>(
    "products",
    mockProducts,
  );
  const [units] = usePersistentMockState<MockUnit[]>("units", mockUnits);
  const [warehouses] = usePersistentMockState<MockWarehouse[]>(
    "warehouses",
    mockWarehouses,
  );

  const factoryOptions = useMemo(
    () => warehouses.filter((w) => w.warehouseType === "raw_material"),
    [warehouses],
  );
  const [factoryWarehouseId, setFactoryWarehouseId] = useState<string>(
    factoryOptions[0]?._id ?? "",
  );

  // Window navigation: shift in 7-day strides.
  const [windowStart, setWindowStart] = useState<number>(() =>
    startOfDay(Date.now() - 2 * DAY),
  );
  const windowEnd = windowStart + 7 * DAY;

  const days = useMemo(() => {
    const out: number[] = [];
    for (let i = 0; i < 7; i += 1) {
      out.push(windowStart + i * DAY);
    }
    return out;
  }, [windowStart]);

  /* Filter WOs visible in the window for the selected factory. */
  const rows = useMemo(
    () =>
      workOrders
        .filter((wo) => wo.factoryWarehouseId === factoryWarehouseId)
        .filter((wo) => wo.plannedEndAt >= windowStart)
        .filter((wo) => wo.plannedStartAt <= windowEnd)
        .sort((a, b) => a.plannedStartAt - b.plannedStartAt),
    [workOrders, factoryWarehouseId, windowStart, windowEnd],
  );

  /* Capacity computation (mirrors the planning.productionCapacity stub). */
  const capacity = useMemo(() => {
    let plannedHours = 0;
    let scheduledHours = 0;
    for (const wo of workOrders) {
      if (wo.factoryWarehouseId !== factoryWarehouseId) continue;
      if (wo.plannedEndAt < windowStart) continue;
      if (wo.plannedStartAt > windowEnd) continue;
      const start = Math.max(wo.plannedStartAt, windowStart);
      const end = Math.min(wo.plannedEndAt, windowEnd);
      const hours = Math.max(0, (end - start) / HOUR);
      plannedHours += hours;
      if (wo.status === "in_progress" || wo.status === "paused") {
        scheduledHours += hours;
      }
    }
    // Capacity ceiling: 7 days × 16 working hours/day for one factory line.
    const capacityCeiling = 7 * 16;
    const utilizationPct =
      capacityCeiling <= 0
        ? 0
        : Math.round((plannedHours / capacityCeiling) * 100);
    return {
      plannedHours: Math.round(plannedHours),
      scheduledHours: Math.round(scheduledHours),
      capacityCeiling,
      utilizationPct,
      conflict: plannedHours > capacityCeiling,
    };
  }, [workOrders, factoryWarehouseId, windowStart, windowEnd]);

  const productsById = useMemo(
    () => new Map(products.map((p) => [p._id, p])),
    [products],
  );
  const unitCodeById = useMemo(
    () => new Map(units.map((u) => [u._id, u.code])),
    [units],
  );

  /* Detail Sheet state. */
  const [detailRow, setDetailRow] = useState<MockWorkOrder | null>(null);

  const factoryWarehouse = useMemo(
    () => warehouses.find((w) => w._id === factoryWarehouseId),
    [warehouses, factoryWarehouseId],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Production Planning"
        description="Schedule visualization for upcoming runs. Bars span planned start to end; click any bar to open the work order."
      />

      {/* Top row — factory selector + window navigator */}
      <section className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Select
            value={factoryWarehouseId}
            onValueChange={setFactoryWarehouseId}
          >
            <SelectTrigger className="w-72">
              <SelectValue placeholder="Pick factory warehouse" />
            </SelectTrigger>
            <SelectContent>
              {factoryOptions.map((w) => (
                <SelectItem key={w._id} value={w._id}>
                  {w.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {factoryWarehouse ? (
            <Badge variant="neutral">{factoryWarehouse.code}</Badge>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setWindowStart((v) => v - 7 * DAY)}
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Prev
          </Button>
          <span className="text-[12px] text-(--text-secondary) tabular-nums">
            {dayLabel(windowStart)} → {dayLabel(windowEnd - DAY)}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setWindowStart((v) => v + 7 * DAY)}
          >
            Next <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setWindowStart(startOfDay(Date.now() - 2 * DAY))}
          >
            Today
          </Button>
        </div>
      </section>

      {/* Gantt grid */}
      <section
        className="overflow-hidden rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-surface)"
        aria-label="Production schedule Gantt"
      >
        {/* Header — 7 day columns */}
        <div className="grid grid-cols-[220px_repeat(7,minmax(0,1fr))] border-b border-(--border-subtle) bg-(--bg-canvas)">
          <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-(--text-tertiary) border-r border-(--border-subtle)">
            Work order
          </div>
          {days.map((d) => (
            <div
              key={d}
              className={cn(
                "px-2 py-2 text-[11px] uppercase tracking-wide text-(--text-tertiary)",
                "border-r border-(--border-subtle) last:border-r-0",
              )}
            >
              {dayLabel(d)}
            </div>
          ))}
        </div>

        {/* Rows */}
        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-[13px] text-(--text-tertiary)">
            No work orders scheduled at {factoryWarehouse?.displayName ?? "this factory"} in this window.
          </div>
        ) : (
          rows.map((wo) => {
            const product = productsById.get(wo.outputProductId);
            // Compute bar position as percentage across the 7-day grid track.
            const startPct =
              (Math.max(0, wo.plannedStartAt - windowStart) /
                (7 * DAY)) *
              100;
            const endPct =
              (Math.min(7 * DAY, wo.plannedEndAt - windowStart) /
                (7 * DAY)) *
              100;
            const widthPct = Math.max(2, endPct - startPct);
            return (
              <div
                key={wo._id}
                className="grid grid-cols-[220px_repeat(7,minmax(0,1fr))] border-b border-(--border-subtle) last:border-b-0 group"
              >
                <div className="px-3 py-3 border-r border-(--border-subtle) flex flex-col gap-0.5">
                  <span className="font-mono text-[11px] text-(--text-tertiary)">
                    {wo.workOrderNumber}
                  </span>
                  <span className="text-[12px] text-(--text-primary) truncate">
                    {product?.displayName ?? "—"}
                  </span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <WorkOrderStatusBadge status={wo.status} size="sm" />
                    <PriorityBadge priority={wo.priority} />
                  </div>
                </div>
                {/* The bar lane (col-span-7, with absolute bar positioned). */}
                <div className="col-span-7 relative h-16">
                  {/* Day grid lines */}
                  {days.map((_, i) => (
                    <span
                      key={i}
                      aria-hidden
                      className="absolute top-0 bottom-0 border-r border-(--border-subtle) last:border-r-0"
                      style={{ left: `${(i / 7) * 100}%`, width: `${100 / 7}%` }}
                    />
                  ))}
                  {/* Bar */}
                  <button
                    type="button"
                    onClick={() => setDetailRow(wo)}
                    className={cn(
                      "absolute top-3 bottom-3 rounded-[var(--radius-sm)] border",
                      "px-2 flex items-center text-[11px] font-medium overflow-hidden",
                      "transition-shadow hover:shadow-(--shadow-sm)",
                    )}
                    style={{
                      left: `${startPct}%`,
                      width: `${widthPct}%`,
                      backgroundColor: STATUS_BAR_BG[wo.status],
                      borderColor: STATUS_BAR_BORDER[wo.status],
                      color: STATUS_BAR_BORDER[wo.status],
                    }}
                    title={`${wo.workOrderNumber} — ${product?.displayName ?? ""}`}
                  >
                    <span className="truncate">
                      {wo.plannedQuantity}{" "}
                      {unitCodeById.get(wo.outputUnitId) ?? "pcs"}
                    </span>
                    {/* Progress overlay for in-progress runs */}
                    {wo.status === "in_progress" && wo.plannedQuantity > 0 ? (
                      <span
                        aria-hidden
                        className="absolute inset-y-0 left-0 bg-(--info)/15 rounded-l-[var(--radius-sm)]"
                        style={{
                          width: `${Math.min(
                            100,
                            (wo.actualQuantity / wo.plannedQuantity) * 100,
                          )}%`,
                        }}
                      />
                    ) : null}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </section>

      {/* Capacity summary */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          caption="Planned hours"
          value={`${capacity.plannedHours} h`}
          hint={`Across ${rows.length} run${rows.length === 1 ? "" : "s"}`}
        />
        <KpiCard
          caption="Scheduled hours"
          value={`${capacity.scheduledHours} h`}
          hint="Active + paused"
        />
        <KpiCard
          caption="Capacity ceiling"
          value={`${capacity.capacityCeiling} h`}
          hint="7d × 16h working"
        />
        <KpiCard
          caption="Utilisation"
          value={`${capacity.utilizationPct}%`}
          hint="Planned ÷ ceiling"
          delta={
            capacity.conflict
              ? {
                  value: "Over capacity",
                  trend: "down",
                  intent: "danger",
                }
              : undefined
          }
        />
      </section>

      {/* Capacity bar viz (planned fill + ceiling line) */}
      <section className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-surface) p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[12px] font-medium text-(--text-primary)">
            Weekly utilisation
          </span>
          {capacity.conflict ? (
            <Badge variant="danger">Conflict — exceeds capacity</Badge>
          ) : capacity.utilizationPct > 80 ? (
            <Badge variant="warning">Heavy load</Badge>
          ) : (
            <Badge variant="success">Healthy</Badge>
          )}
        </div>
        {/* SVG capacity bar: planned (filled) + ceiling marker line. */}
        <svg
          viewBox="0 0 100 12"
          preserveAspectRatio="none"
          className="w-full h-3"
          aria-hidden
        >
          {/* Track */}
          <rect x={0} y={0} width={100} height={12} fill="var(--bg-canvas)" />
          {/* Planned fill */}
          <rect
            x={0}
            y={0}
            width={Math.min(100, capacity.utilizationPct)}
            height={12}
            fill={
              capacity.conflict
                ? "var(--danger)"
                : capacity.utilizationPct > 80
                  ? "var(--warning)"
                  : "var(--info)"
            }
          />
          {/* Capacity ceiling marker (always at 100%) */}
          <line
            x1={100}
            x2={100}
            y1={0}
            y2={12}
            stroke="var(--border-strong)"
            strokeWidth={0.5}
          />
        </svg>
        <div className="flex justify-between mt-1 text-[11px] text-(--text-tertiary) tabular-nums">
          <span>0 h</span>
          <span>{capacity.plannedHours} h planned</span>
          <span>{capacity.capacityCeiling} h ceiling</span>
        </div>
      </section>

      {/* Detail Sheet — re-uses the simple read-only summary; full detail
          lives on the work-orders page. Here we just preview + link. */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open) => !open && setDetailRow(null)}
      >
        <SheetContent side="right" size="md">
          {detailRow ? (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono">
                    {detailRow.workOrderNumber}
                  </span>
                  <WorkOrderStatusBadge status={detailRow.status} />
                  <PriorityBadge priority={detailRow.priority} />
                </SheetTitle>
                <SheetDescription>
                  {productsById.get(detailRow.outputProductId)?.displayName ??
                    "—"}{" "}
                  · {detailRow.plannedQuantity} planned units
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <Metric
                    label="Planned start"
                    value={dayLabel(detailRow.plannedStartAt)}
                  />
                  <Metric
                    label="Planned end"
                    value={dayLabel(detailRow.plannedEndAt)}
                  />
                  <Metric
                    label="Actual"
                    value={`${detailRow.actualQuantity}/${detailRow.plannedQuantity}`}
                  />
                  <Metric
                    label="Yield"
                    value={
                      detailRow.plannedQuantity > 0
                        ? `${Math.round((detailRow.actualQuantity / detailRow.plannedQuantity) * 100)}%`
                        : "—"
                    }
                  />
                </div>
                {detailRow.notes ? (
                  <div className="rounded-[var(--radius-md)] bg-(--bg-canvas) p-3 text-[12px] text-(--text-secondary) whitespace-pre-line">
                    {detailRow.notes}
                  </div>
                ) : null}
                <p className="mt-4 text-[12px] text-(--text-tertiary)">
                  Open the Work Orders page to start, pause, record production
                  or cancel this run.
                </p>
              </SheetBody>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </span>
      <span className="text-[13px] font-mono tabular-nums text-(--text-primary)">
        {value}
      </span>
    </div>
  );
}
